// Serviço unificado para WebSocket
import io from 'socket.io-client';
import { resolveApiBaseUrl } from './api';

function isPrivateOrLocalHost(hostname) {
  if (!hostname) return false;
  const h = String(hostname).toLowerCase();
  if (h === 'localhost' || h === '127.0.0.1' || h === '::1') return true;
  if (/^10\./.test(h)) return true;
  if (/^192\.168\./.test(h)) return true;
  if (/^172\.(1[6-9]|2\d|3[0-1])\./.test(h)) return true;
  return false;
}

// Helpers para construir lista de candidatos de URL de socket
function normalizeBaseUrl(url) {
  return String(url || '').trim().replace(/\/+$/, '');
}

function sanitizeBaseUrl(url) {
  const normalized = normalizeBaseUrl(url);
  if (!normalized) return '';
  try {
    const parsed = new URL(normalized);
    if (parsed.protocol === 'http:' || parsed.protocol === 'https:') return parsed.origin;
  } catch (_) {}
  return '';
}

function deriveBackendFromOrigin(origin) {
  const normalized = sanitizeBaseUrl(origin);
  if (!normalized) return '';
  try {
    const url = new URL(normalized);
    if (url.port === '3000') url.port = '5000';
    if (url.port === '3001') url.port = '5001';
    return url.origin;
  } catch (_) {
    return normalized;
  }
}

function buildSocketCandidates() {
  const cachedApiBase = (typeof localStorage !== 'undefined')
    ? sanitizeBaseUrl(localStorage.getItem('apiBaseUrl') || '')
    : '';

  const currentOrigin = sanitizeBaseUrl(window.location.origin || '');
  const derivedFromOrigin = deriveBackendFromOrigin(currentOrigin);
  const envSocket = sanitizeBaseUrl(process.env.REACT_APP_SOCKET_URL);
  const envApi = sanitizeBaseUrl(process.env.REACT_APP_API_URL);
  const resolvedApi = sanitizeBaseUrl(resolveApiBaseUrl());

  const isLocalHost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const cachedNormalized = String(cachedApiBase || '').replace(/\/$/, '');
  const safeCached = (() => {
    if (!cachedNormalized) return '';
    if (cachedNormalized === currentOrigin) return '';
    if (isLocalHost && !cachedNormalized.includes('localhost') && !cachedNormalized.includes('127.0.0.1')) return '';
    try {
      const parsedCached = new URL(cachedNormalized);
      const parsedCurrent = new URL(currentOrigin);
      const cachedHost = parsedCached.hostname;
      const currentHost = parsedCurrent.hostname;
      if (!isPrivateOrLocalHost(currentHost) && isPrivateOrLocalHost(cachedHost) && cachedHost !== currentHost) {
        return '';
      }
      const cachedPort = parsedCached.port || (parsedCached.protocol === 'https:' ? '443' : '80');
      const currentPort = parsedCurrent.port || (parsedCurrent.protocol === 'https:' ? '443' : '80');
      const isDevFrontendPort = currentPort === '3000' || currentPort === '3001';
      if (!isLocalHost && !isDevFrontendPort && cachedHost === currentHost && cachedPort === '5000' && currentPort !== '5000') {
        return '';
      }
      if (
        isPrivateOrLocalHost(currentHost) &&
        isPrivateOrLocalHost(cachedHost) &&
        cachedHost !== currentHost
      ) {
        return '';
      }
    } catch (_) {}
    return cachedNormalized;
  })();

  const currentPort = (() => {
    try { return new URL(currentOrigin).port || ''; } catch (_) { return ''; }
  })();
  const isDevFrontendPort = currentPort === '3000' || currentPort === '3001';

  if (!isLocalHost && !isDevFrontendPort) {
    return [currentOrigin];
  }

  const raw = [
    envSocket,
    resolvedApi,
    envApi,
    safeCached,
    derivedFromOrigin,
    currentOrigin
  ].filter(Boolean);

  const normalized = Array.from(new Set(raw.map(u => u.replace(/\/$/, ''))));
  return normalized.length ? normalized : [derivedFromOrigin || currentOrigin];
}

class UnifiedSocketService {
  constructor() {
    this.socket = null;
    this.signature = null;
  }

  connect(user) {
    const candidates = buildSocketCandidates();

    const userData = JSON.parse(localStorage.getItem('vortiqaUser') || '{}');
    const token = userData.token || user?.token;
    if (!token || token === 'undefined' || token === 'null') {
      if (this.socket) {
        try { this.socket.disconnect(); } catch {}
        this.socket = null;
      }
      this.signature = null;
      return null;
    }
    const signature = `${String(user?.id ?? 'anonymous')}|${String(user?.companyId ?? 1)}|${String(token ?? '')}`;

    if (this.socket && this.signature === signature) {
      return this.socket;
    }
    this.signature = signature;

    const options = {
      path: '/socket.io',
      transports: ['polling', 'websocket'],
      withCredentials: true,
      auth: {
        token,
        companyId: user?.companyId || 1,
        userId: user?.id || 'anonymous'
      },
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionAttempts: 10,
      timeout: 30000,
      forceNew: false
    };

    const tryConnect = (index) => {
      if (index >= candidates.length) {
        return;
      }

      const backendUrl = candidates[index];

      // Desconectar socket anterior se existir
      if (this.socket) {
        try { this.socket.disconnect(); } catch {}
        this.socket = null;
      }

      this.socket = io(backendUrl, options);
      const s = this.socket;

      const onConnect = () => {
        try { localStorage.setItem('apiBaseUrl', backendUrl); } catch {}
        // Remover handlers de erro (não mais necessários)
        s.off('connect_error', onConnectError);
        s.off('error', onError);
      };

      const onConnectError = (error) => {
        const msg = String(error?.message || '');
        if (/ERR_CONNECTION_REFUSED|xhr poll error|transport error/i.test(msg)) {
          try {
            const cached = String(localStorage.getItem('apiBaseUrl') || '').replace(/\/$/, '');
            if (cached && cached === String(backendUrl || '').replace(/\/$/, '')) {
              localStorage.removeItem('apiBaseUrl');
            }
          } catch {}
        }
        try { s.off('connect', onConnect); s.off('error', onError); s.off('connect_error', onConnectError); } catch {}
        try { s.disconnect(); } catch {}
        const nextIndex = index + 1;
        tryConnect(nextIndex);
      };

      const onError = (error) => {
        onConnectError(error);
      };

      s.on('connect', onConnect);
      s.on('disconnect', (reason) => {
      });
      s.on('connect_error', onConnectError);
      s.on('error', onError);
    };

    tryConnect(0);
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.signature = null;
  }

  getSocket() {
    return this.socket;
  }
}

const unifiedSocketService = new UnifiedSocketService();
export default unifiedSocketService;
